import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { RefreshCw } from 'lucide-react'
import { errorMessage } from '@/api/client'
import { useMeta } from '@/api/queries'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { AuthContext, type AuthInfo } from './context'
import { ChangePasswordScreen, SignInScreen } from './AuthScreens'
import { bootstrap, type BootResult } from './bootstrap'
import { signOut } from './local'

type GateState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; result: BootResult }

/** Loads /api/meta, signs in when the backend requires it (Entra ID or local accounts), then renders the app. */
export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>({ status: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    bootstrap(attempt > 0)
      .then((result) => {
        if (!cancelled) setState({ status: 'ready', result })
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ status: 'error', message: errorMessage(error) })
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  const session = state.status === 'ready' ? state.result.session : null
  const info = useMemo<AuthInfo>(() => {
    if (session?.mode === 'entra') {
      return { mode: 'entra', accountName: session.accountName, signOut: session.signOut }
    }
    if (session?.mode === 'local') return { mode: 'local', accountName: null, signOut: () => void signOut() }
    return { mode: 'none', accountName: null, signOut: null }
  }, [session])

  if (state.status === 'error') {
    return (
      <FullScreen>
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold">Can&rsquo;t reach the knowledge assistant</h1>
          <p className="mt-2 text-sm text-ink-muted">{state.message}</p>
          <Button
            className="mt-5"
            variant="secondary"
            onClick={() => {
              setState({ status: 'loading' })
              setAttempt((n) => n + 1)
            }}
          >
            <RefreshCw className="size-4" aria-hidden />
            Try again
          </Button>
        </div>
      </FullScreen>
    )
  }

  if (state.status === 'loading' || !session || session.mode === 'redirecting') {
    const label = session?.mode === 'redirecting' ? 'Redirecting to sign-in…' : 'Loading…'
    return (
      <FullScreen>
        <div className="flex items-center gap-3 text-sm text-ink-muted" role="status">
          <Spinner className="size-4" />
          {label}
        </div>
      </FullScreen>
    )
  }

  if (session.mode === 'local') {
    return (
      <AuthContext.Provider value={info}>
        <LocalGate>{children}</LocalGate>
      </AuthContext.Provider>
    )
  }

  const content = <AuthContext.Provider value={info}>{children}</AuthContext.Provider>
  if (session.mode === 'entra') {
    const { Provider, instance } = session
    return <Provider instance={instance}>{content}</Provider>
  }
  return content
}

/** Local accounts: sign in, then replace a temporary password, then the app. */
function LocalGate({ children }: { children: ReactNode }) {
  const meta = useMeta()
  if (!meta.user) return <SignInScreen />
  if (meta.user.must_change_password) return <ChangePasswordScreen />
  return <>{children}</>
}

function FullScreen({ children }: { children: ReactNode }) {
  return <div className="flex h-full items-center justify-center bg-canvas p-6">{children}</div>
}
